import { useState } from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { localeDateFormat } from '../../../utils/localeUtils'
import { OpenAbsenceResponse } from '../../../types/open-absence'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import Modal from '../../../shared/UI/Modal'
import { manualRequestsService } from '../../../services/myActionsService'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import { DateFormats } from '../../../api/absence/tempTypes/generalprops'
import {
  AbsenceCreateRequest,
  AbsenceOccurrence,
  WFMAbsenceActionedPayload,
} from '../../../types/absence'
import UserErrorMessage from '../../../utils/errorFilter'
import { RootStore, useAppDispatch } from '../../../redux/store'
import { hideDrawer, hideHeaderDrawer } from '../../../redux/reducers/appSettingsReducer'
import useWfmAbsence from '../../../components/AbsenceDrawerComponent/WfmAbsenceDrawer/Hooks/useWfmAbsence'
import actionsService from '../../../services/actionsService'

type Props = {
  openAbsences: OpenAbsenceResponse[]
  open: boolean
  onClose: () => void
  onClick: () => void
  wfmAbsenceId: number
}

interface SelectAbsenceData {
  id: number
  statusName: string
}

function OpenAbsences({ openAbsences, open, onClose, onClick, wfmAbsenceId }: Props) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isLoadingAbsence, setIsLoadingAbsence] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)

  const { wfmAbsence, unlockWfmAbsence } = useWfmAbsence(wfmAbsenceId)

  const getAbsenceData = async (absenceId: number): Promise<AbsenceOccurrence | null> => {
    setIsLoadingAbsence(true)
    try {
      const result = await manualRequestsService.getAbsenceOccurrence(absenceId)
      return result.absenceOccurrence
    } catch (err: any) {
      const response = err.response?.data
      if (response?.errors) {
        response.errors.forEach((error: any) => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      } else {
        dispatch(showErrorMessage('Failed to get absence details'))
      }
      return null
    } finally {
      setIsLoadingAbsence(false)
    }
  }

  const formatDate = (date: Date) => {
    const dateToFormat = new Date(date)
    return format(dateToFormat, localeDateFormat)
  }

  const extractAbsenceData = (absences: OpenAbsenceResponse[]): SelectAbsenceData[] =>
    absences.map(e => ({
      id: e.id,
      statusName: `${e.days[0].absenceType.reason.description}
                         ${formatDate(e.days[0].absenceDate)} - ${formatDate(e.expectedReturn)}`,
    }))

  const extendAbsence = async (absenceId: number) => {
    if (!absenceId || !wfmAbsence) {
      dispatch(showErrorMessage('Missing required data for absence extension'))
      return
    }

    setIsSubmitting(true)

    try {
      const baseUrl = 'myactions'
      const type = 'absenceoccurrence'
      const openAbsenceExtension = 'true'
      const navigationUrl = `/${baseUrl}/${type}/${absenceId}/?isAbsenceExtension=${openAbsenceExtension}&wfmId=${wfmAbsenceId}`

      navigate(navigationUrl)
    } catch (err: any) {
      const response = err.response?.data
      if (response?.errors) {
        response.errors.forEach((error: any) => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      } else {
        dispatch(showErrorMessage('Failed to extend absence'))
      }
    } finally {
      unlockWfmAbsence()
      setIsSubmitting(false)
      dispatch(hideHeaderDrawer())
      dispatch(hideDrawer())
      onClose()
    }
  }

  const onAbsenceClose = () => {
      unlockWfmAbsence()
      onClose()
  }

  const isDropdownDisabled = !wfmAbsence || isSubmitting || isLoadingAbsence

  return (
    <Modal
      data-testid="open-absences-modal"
      type="question"
      open={open}
      onClick={onClick}
      title="Extend existing or create new?"
      buttonLabel="Create absence"
      isDisabled={isSubmitting}
      message="Select an absence to extend it"
      maxWidth="md"
      subMessage="Or"
      dropdown={
        <div style={{ width: '100%' }}>
          <DropdownMenu
            label="Existing Absences"
            id="existing-absences"
            data={extractAbsenceData(openAbsences)}
            textField="statusName"
            valueField="id"
            onChange={e => {
              extendAbsence(Number(e.target.value))
            }}
            helperText="Select Absence"
            disabled={isDropdownDisabled}
          />
          {(isSubmitting || isLoadingAbsence) && (
            <div style={{ marginTop: '8px', color: '#1976d2' }}>Extending absence...</div>
          )}
        </div>
      }
      onClose={onAbsenceClose}
    />
  )
}

export default OpenAbsences
